import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, PlusCircle, UserCog } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import AddConnectionModal from './AddConnectionModal';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import ConnectionStringCell from './ConnectionStringCell';
import GenericConnectorForm from './components/GenericConnectorForm';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';
import SourceTypeSelectorModal from '../configurations/ingestion/popups/SourceTypeSelectorModal';
import { apiService } from './connectionsApiService';
const ConnectionsPage: React.FC = () => {
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [editingConnection, setEditingConnection] = useState<any | null>(null);
  const [connections, setConnections] = useState<any[]>([]);
  const [showAssignmentModal, setAssignmentModal] = useState(false);




  const fetchConnections = async () => {
    try {
      const data = await apiService.get('/DataStores', {}, 'backend');
      setConnections(data.datastores || []);
    } catch (err) {
      console.error('Error fetching connections:', err);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  const handleAddConnection = (data: any) => {
    // Data is coming from handleFormSubmit in GenericConnectorForm (API response)
    console.log('Successfully created connection:', data);
    fetchConnections(); // Refresh the list
    setSelectedConnector(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Connections</h2>
          <p className="text-sm text-gray-400">Manage environment-based connection strings.</p>
        </div>
        <button
          onClick={() => setShowTypeSelector(true)}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow transition-all active:scale-95"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add New Connection
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-neutral-800 rounded-xl bg-neutral-900/50 backdrop-blur-sm">
        <table className="min-w-full text-sm text-left text-gray-300">
          <thead className="bg-neutral-900/80 text-gray-400">
            <tr>
              <th className="px-6 py-4 font-bold border-b border-neutral-800">Name</th>
              <th className="px-6 py-4 font-bold border-b border-neutral-800">Tags & Type</th>
              <th className="px-6 py-4 font-bold border-b border-neutral-800">Status</th>
              <th className="px-6 py-4 font-bold border-b border-neutral-800">Environment</th>
              <th className="px-6 py-4 font-bold border-b border-neutral-800">Last Modified</th>
              <th className="px-6 py-4 font-bold border-b border-neutral-800 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {connections.map((conn) => (
              <tr key={conn.id} className="hover:bg-indigo-500/5 transition-colors">
                <td className="px-6 py-4 font-medium text-white">{conn.name}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-1 flex-wrap">
                    {conn.tags?.map((tag: string) => (
                      <span key={tag} className="px-2 py-0.5 bg-neutral-800 rounded text-[10px] uppercase font-bold text-gray-400 border border-neutral-700">
                        {tag}
                      </span>
                    ))}
                    <span className="px-2 py-0.5 bg-indigo-500/10 rounded text-[10px] uppercase font-bold text-indigo-400 border border-indigo-500/20">
                      {conn.connectorKey}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${conn.status === 'Active' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                    {conn.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-400 font-mono text-xs">{conn.environment || 'N/A'}</td>
                <td className="px-6 py-4 text-gray-400 text-xs">
                  {new Date(conn.updatedAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-3">
                    <button
                      className="text-gray-400 hover:text-indigo-400 transition-colors"
                      title="Manage Permissions & Access"
                      onClick={() => setAssignmentModal(true)}
                    >
                      <UserCog size={16} />
                    </button>
                    <button
                      className="text-gray-400 hover:text-yellow-400 transition-colors"
                      title="Edit"
                      onClick={() => {
                        setEditingConnection(conn);
                        // Map connectorKey back to display name if needed, or use a map
                        // GenericConnectorForm uses display name for title but key for template loading
                        // Wait, looking at GenericConnectorForm logic...
                      }}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this connection?')) {
                          await apiService.delete(`/DataStores/${conn.id}`, 'backend');
                          fetchConnections();
                        }
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTypeSelector && (
        <SourceTypeSelectorModal
          onClose={() => setShowTypeSelector(false)}
          onSelect={(sourceType) => {
            setSelectedConnector(sourceType);
            setShowTypeSelector(false);
          }}
          currentSelection={""}
        />
      )}

      {(selectedConnector || editingConnection) && (
        <GenericConnectorForm
          connectorKey={selectedConnector || editingConnection?.connectorKey}
          onClose={() => {
            setSelectedConnector(null);
            setEditingConnection(null);
          }}
          onSubmit={handleAddConnection}
          initialData={editingConnection}
        />
      )}


      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => { }}
          entityType="connections"
          entityName="Connection & Keys"
        />
      )}
    </div>
  );
};

export default ConnectionsPage;
